// El estado de cada modal es un objeto reactive que se lee antes del await y se
// escribe despues; la regla lo marca como carrera aunque cada modal tiene una
// sola accion en curso (el boton queda deshabilitado mientras corre).
/* eslint-disable require-atomic-updates */
import { computed, reactive, ref } from "vue";
import { useStore } from 'vuex';

const ORDEN = ['paises', 'departamentos', 'ciudades', 'zonas', 'barrios'];

const NIVEL_INFO = {
    paises:        { singular: 'País',         plural: 'Países',         icon: 'fas fa-globe-americas' },
    departamentos: { singular: 'Departamento', plural: 'Departamentos',  icon: 'fas fa-map' },
    ciudades:      { singular: 'Ciudad',       plural: 'Ciudades',       icon: 'fas fa-city' },
    zonas:         { singular: 'Zona',         plural: 'Zonas',          icon: 'fas fa-draw-polygon' },
    barrios:       { singular: 'Barrio',       plural: 'Barrios',        icon: 'fas fa-home' },
};

const EXTRA_LABELS = {
    iso2: 'ISO2',
    iso3: 'ISO3',
    phonecode: 'Indicativo',
    state: 'Estado',
    descripcion: 'Descripción',
};

// Tablas que aparecen en `uso.detalle` ("inmuebles.barrio_id", "barrios", ...)
const USO_LABELS = {
    inmuebles: 'Inmuebles',
    requerimientos: 'Requerimientos',
    userdata: 'Usuarios',
    empresa_agente: 'Empresas',
    clientes: 'Clientes',
    ciencuadra_localidades: 'Ciencuadras',
    barrio: 'Barrios',
    zona: 'Zonas',
    cities: 'Ciudades',
    states: 'Departamentos',
};

const MENSAJES_CODIGO = {
    NO_AUTORIZADO: 'Solo un super admin puede modificar localidades',
    NO_EXISTE: 'La localidad ya no existe',
    DUPLICADO: 'Ya existe una localidad con ese nombre bajo el mismo padre',
    PADRE_NO_EXISTE: 'El padre seleccionado no existe',
    DESTINO_INVALIDO: 'No puedes fusionar una localidad consigo misma',
    DESTINO_NO_EXISTE: 'La localidad destino no existe en ese nivel',
    PORTAL_NO_EXISTE: 'Alguno de los portales no existe',
};

const columnaVacia = () => ({
    items: [], q: '', estado: '', page: 1, lastPage: 1, total: 0, loading: false,
});

const camposVacios = () => ({
    name: '', iso2: '', iso3: '', phonecode: '', state: 'activo', descripcion: '',
});

// Normaliza para comparar lo que vino de la API con lo que hay en el form
const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());

export function useLocalidades() {
    const store = useStore();
    const alertTable = ref({ state: false, message: '', color: 'success' });

    let alertTimer = null;
    const showAlert = (message, color = 'success') => {
        if (alertTimer) clearTimeout(alertTimer);
        alertTable.value = { state: true, message, color };
        alertTimer = setTimeout(() => { alertTable.value.state = false; }, 6000);
    };

    // --- Errores ---------------------------------------------------------
    const codigoError = (error) => error.response?.data?.errorMessage?.code || null;

    const mensajeError = (error, fallback) => {
        const codigo = codigoError(error);
        const payload = error.response?.data;
        if (codigo && MENSAJES_CODIGO[codigo]) return MENSAJES_CODIGO[codigo];
        if (typeof payload?.data === 'string' && payload.data) return payload.data;
        if (typeof payload?.message === 'string' && payload.message) return payload.message;
        return fallback;
    };

    // 422 de validacion: errorMessage trae { campo: [mensajes] }
    const erroresValidacion = (error) => {
        const em = error.response?.data?.errorMessage;
        if (error.response?.status !== 422 || !em || typeof em !== 'object' || em.code) return {};
        const errores = {};
        Object.entries(em).forEach(([campo, msgs]) => {
            errores[campo] = Array.isArray(msgs) ? msgs[0] : String(msgs);
        });
        return errores;
    };

    // --- Configuracion ---------------------------------------------------
    const niveles = computed(() => store.state.appLocalidades.niveles);
    const portales = computed(() => store.state.appLocalidades.portales);
    const puedeEditar = computed(() => store.state.appLocalidades.puedeEditar);

    const configDe = (nivel) => niveles.value.find((n) => n.nivel === nivel) || {};
    const padreDe = (nivel) => configDe(nivel).padre || null;
    const hijoDe = (nivel) => configDe(nivel).hijo || null;
    const extrasDe = (nivel) => configDe(nivel).extras || [];
    const infoNivel = (nivel) => NIVEL_INFO[nivel] || { singular: nivel, plural: nivel, icon: 'fas fa-map-marker-alt' };
    const extraLabel = (campo) => EXTRA_LABELS[campo] || campo;

    const descendientesDe = (nivel) => ORDEN.slice(ORDEN.indexOf(nivel) + 1);

    // --- Columnas en cascada ---------------------------------------------
    const columnas = reactive(Object.fromEntries(ORDEN.map((n) => [n, columnaVacia()])));
    // Seleccion por nivel: { id, name } o null
    const seleccion = reactive(Object.fromEntries(ORDEN.map((n) => [n, null])));
    // Para descartar respuestas viejas cuando el usuario escribe rapido
    const peticiones = Object.fromEntries(ORDEN.map((n) => [n, 0]));

    const columnasVisibles = computed(() =>
        ORDEN.filter((n) => !padreDe(n) || seleccion[padreDe(n)])
    );

    const cargarColumna = async (nivel, { append = false } = {}) => {
        const col = columnas[nivel];
        const padre = padreDe(nivel);
        if (padre && !seleccion[padre]) {
            Object.assign(col, columnaVacia());
            return;
        }

        const params = {
            per_page: nivel === 'paises' ? 200 : 50,
            page: append ? col.page : 1,
        };
        if (!append) col.page = 1;
        if (col.q.trim()) params.q = col.q.trim();
        if (padre) params.parent_id = seleccion[padre].id;
        if (nivel === 'paises' && col.estado) params.estado = col.estado;

        const peticion = ++peticiones[nivel];
        col.loading = true;
        try {
            const data = await store.dispatch('appLocalidades/listarAction', { nivel, params });
            if (peticion !== peticiones[nivel]) return;
            const pagina = data.items || {};
            col.items = append ? [...col.items, ...(pagina.data || [])] : (pagina.data || []);
            col.lastPage = pagina.last_page || 1;
            col.total = pagina.total || 0;
        } catch (error) {
            if (peticion !== peticiones[nivel]) return;
            showAlert(mensajeError(error, `Error al cargar ${infoNivel(nivel).plural.toLowerCase()}`), 'danger');
        } finally {
            if (peticion === peticiones[nivel]) col.loading = false;
        }
    };

    const cargarMas = (nivel) => {
        const col = columnas[nivel];
        if (col.page >= col.lastPage || col.loading) return;
        col.page += 1;
        cargarColumna(nivel, { append: true });
    };

    const timers = {};
    const buscarEnColumna = (nivel) => {
        if (timers[nivel]) clearTimeout(timers[nivel]);
        timers[nivel] = setTimeout(() => cargarColumna(nivel), 350);
    };

    const limpiarDescendientes = (nivel) => {
        descendientesDe(nivel).forEach((d) => {
            seleccion[d] = null;
            peticiones[d] += 1;
            Object.assign(columnas[d], columnaVacia());
        });
    };

    // --- Detalle ---------------------------------------------------------
    const detalle = ref(null);
    const loadingDetalle = ref(false);

    const abrirDetalle = async (nivel, id) => {
        loadingDetalle.value = true;
        try {
            detalle.value = await store.dispatch('appLocalidades/detalleAction', { nivel, id });
        } catch (error) {
            detalle.value = null;
            showAlert(mensajeError(error, 'Error al cargar el detalle'), 'danger');
        } finally {
            loadingDetalle.value = false;
        }
    };

    const seleccionar = (nivel, item) => {
        seleccion[nivel] = { id: item.id, name: item.name };
        limpiarDescendientes(nivel);
        const hijo = hijoDe(nivel);
        if (hijo) cargarColumna(hijo);
        abrirDetalle(nivel, item.id);
    };

    // Reconstruye la cascada a partir de una `ruta` (migas del detalle).
    // Se usa tras crear, mover o fusionar, y al hacer clic en una miga.
    const abrirRuta = async (ruta) => {
        if (!ruta?.length) return;
        const ultimo = ruta[ruta.length - 1];

        ORDEN.forEach((n) => { seleccion[n] = null; });
        ruta.forEach((r) => { seleccion[r.nivel] = { id: r.id, name: r.name }; });

        // Se recargan todas las columnas: los conteos pudieron cambiar
        const hasta = ORDEN.indexOf(hijoDe(ultimo.nivel) || ultimo.nivel);
        ORDEN.forEach((n, i) => {
            if (i > hasta) {
                peticiones[n] += 1;
                Object.assign(columnas[n], columnaVacia());
            } else {
                columnas[n].q = '';
            }
        });

        await Promise.all([
            ...ORDEN.slice(0, hasta + 1).map((n) => cargarColumna(n)),
            abrirDetalle(ultimo.nivel, ultimo.id),
        ]);
    };

    const irAMiga = (index) => {
        if (!detalle.value?.ruta) return;
        abrirRuta(detalle.value.ruta.slice(0, index + 1));
    };

    const cerrarDetalle = () => { detalle.value = null; };

    // Campos extra con valor para mostrar en el detalle
    const extrasDetalle = computed(() => {
        if (!detalle.value) return [];
        return extrasDe(detalle.value.nivel).map((campo) => ({
            campo,
            label: extraLabel(campo),
            valor: detalle.value.item?.[campo],
        }));
    });

    const usoLabel = (clave) => {
        const [tabla, columna] = clave.split('.');
        const base = USO_LABELS[tabla] || NIVEL_INFO[tabla]?.plural || tabla;
        return columna ? `${base} · ${columna}` : `${base} (hijos)`;
    };

    const usoEntradas = (uso) =>
        Object.entries(uso?.detalle || {})
            .map(([clave, total]) => ({ clave, label: usoLabel(clave), total: Number(total || 0) }))
            .sort((a, b) => b.total - a.total);

    // --- Crear / editar --------------------------------------------------
    const form = reactive({
        active: false,
        modo: 'crear',
        nivel: 'paises',
        id: null,
        original: null,
        campos: camposVacios(),
        padreActual: null,
        nuevoPadre: null,
        moviendo: false,
        errores: {},
        error: '',
        guardando: false,
    });

    const abrirCrear = (nivel) => {
        const padre = padreDe(nivel);
        Object.assign(form, {
            active: true,
            modo: 'crear',
            nivel,
            id: null,
            original: null,
            campos: camposVacios(),
            padreActual: padre ? seleccion[padre] : null,
            nuevoPadre: null,
            moviendo: false,
            errores: {},
            error: '',
        });
    };

    const abrirEditar = () => {
        if (!detalle.value) return;
        const { nivel, item, ruta } = detalle.value;
        const campos = camposVacios();
        Object.keys(campos).forEach((k) => {
            if (item[k] !== undefined && item[k] !== null) campos[k] = item[k];
        });
        const padreRuta = ruta && ruta.length > 1 ? ruta[ruta.length - 2] : null;
        Object.assign(form, {
            active: true,
            modo: 'editar',
            nivel,
            id: item.id,
            original: { ...item },
            campos,
            padreActual: padreRuta ? { id: padreRuta.id, name: padreRuta.name } : null,
            nuevoPadre: null,
            moviendo: false,
            errores: {},
            error: '',
        });
    };

    const cerrarForm = () => { form.active = false; };

    const construirPayload = () => {
        const extras = extrasDe(form.nivel);
        const payload = {};

        if (form.modo === 'crear') {
            payload.name = form.campos.name.trim();
            if (form.padreActual) payload.parent_id = form.padreActual.id;
            extras.forEach((campo) => {
                const v = norm(form.campos[campo]);
                if (v !== '') payload[campo] = v;
            });
            return payload;
        }

        // Editar: solo lo que cambio
        const original = form.original || {};
        if (norm(form.campos.name) !== norm(original.name)) payload.name = form.campos.name.trim();
        extras.forEach((campo) => {
            const v = norm(form.campos[campo]);
            if (v !== norm(original[campo])) payload[campo] = v === '' ? null : v;
        });
        if (form.moviendo && form.nuevoPadre && form.nuevoPadre.id !== form.padreActual?.id) {
            payload.parent_id = form.nuevoPadre.id;
        }
        return payload;
    };

    const guardarForm = async () => {
        form.errores = {};
        form.error = '';

        const nombre = form.campos.name.trim();
        if (nombre.length < 2 || nombre.length > 191) {
            form.errores = { name: 'El nombre debe tener entre 2 y 191 caracteres' };
            return;
        }
        if (form.modo === 'editar' && form.moviendo && !form.nuevoPadre) {
            form.error = `Selecciona el ${infoNivel(padreDe(form.nivel)).singular.toLowerCase()} destino o cancela el movimiento`;
            return;
        }

        const payload = construirPayload();
        if (form.modo === 'editar' && !Object.keys(payload).length) {
            cerrarForm();
            return;
        }

        form.guardando = true;
        try {
            const data = form.modo === 'crear'
                ? await store.dispatch('appLocalidades/crearAction', { nivel: form.nivel, payload })
                : await store.dispatch('appLocalidades/actualizarAction', { nivel: form.nivel, id: form.id, payload });

            const singular = infoNivel(form.nivel).singular;
            showAlert(
                form.modo === 'crear'
                    ? `${singular} "${data.item?.name || nombre}" creado(a)`
                    : payload.parent_id
                        ? `${singular} movido(a) con todo lo que tiene debajo`
                        : `${singular} actualizado(a)`,
                'success'
            );
            form.active = false;
            await abrirRuta(data.ruta);
        } catch (error) {
            const errores = erroresValidacion(error);
            if (Object.keys(errores).length) {
                form.errores = errores;
            } else {
                form.error = mensajeError(error, 'No se pudo guardar la localidad');
            }
        } finally {
            form.guardando = false;
        }
    };

    // --- Borrar / fusionar -----------------------------------------------
    const borrado = reactive({
        active: false,
        nivel: null,
        item: null,
        uso: null,
        loadingUso: false,
        destino: null,
        buscarEnTodos: false,
        procesando: false,
        error: '',
    });

    const abrirBorrar = async () => {
        if (!detalle.value) return;
        const { nivel, item } = detalle.value;
        Object.assign(borrado, {
            active: true, nivel, item, uso: null, loadingUso: true,
            destino: null, buscarEnTodos: false, procesando: false, error: '',
        });
        try {
            borrado.uso = await store.dispatch('appLocalidades/usoAction', { nivel, id: item.id });
        } catch (error) {
            borrado.error = mensajeError(error, 'No se pudo consultar el uso');
        } finally {
            borrado.loadingUso = false;
        }
    };

    const cerrarBorrar = () => { borrado.active = false; };

    const enUso = computed(() => Number(borrado.uso?.total || 0) > 0);

    const confirmarBorrar = async () => {
        if (enUso.value && !borrado.destino) {
            borrado.error = 'Está en uso: elige a qué localidad reasignar lo que la usa';
            return;
        }
        const { nivel, item } = borrado;
        borrado.procesando = true;
        borrado.error = '';
        try {
            const data = await store.dispatch('appLocalidades/eliminarAction', {
                nivel,
                id: item.id,
                reasignarA: borrado.destino?.id || null,
            });
            borrado.active = false;

            if (data?.destino) {
                const total = Object.values(data.reasignados || {}).reduce((s, n) => s + Number(n || 0), 0);
                showAlert(`"${item.name}" fusionado(a) con "${data.destino.name}" (${total} registro(s) reasignados)`, 'success');
                const destino = await store.dispatch('appLocalidades/detalleAction', {
                    nivel: data.destino.nivel,
                    id: data.destino.id,
                });
                await abrirRuta(destino.ruta);
            } else {
                showAlert(`"${item.name}" eliminado(a)`, 'success');
                seleccion[nivel] = null;
                limpiarDescendientes(nivel);
                detalle.value = null;
                await cargarColumna(nivel);
            }
        } catch (error) {
            if (codigoError(error) === 'EN_USO') {
                // El uso cambio desde que abrimos el modal
                borrado.uso = error.response.data.errorMessage.uso || borrado.uso;
                borrado.error = 'La localidad está en uso: elige a qué localidad reasignar lo que la usa';
            } else {
                borrado.error = mensajeError(error, 'No se pudo eliminar la localidad');
            }
        } finally {
            borrado.procesando = false;
        }
    };

    // --- Homologacion con portales ---------------------------------------
    const homologacion = reactive({
        active: false,
        codigos: {},
        guardando: false,
        error: '',
    });

    const nombrePortal = (id) => portales.value.find((p) => String(p.id) === String(id))?.name || `Portal #${id}`;

    const abrirHomologar = () => {
        if (!detalle.value) return;
        const codigos = {};
        portales.value.forEach((p) => { codigos[p.id] = ''; });
        (detalle.value.portales || []).forEach((p) => { codigos[p.portal_id] = p.codigo || ''; });
        Object.assign(homologacion, { active: true, codigos, guardando: false, error: '' });
    };

    const cerrarHomologar = () => { homologacion.active = false; };

    const guardarHomologacion = async () => {
        const { nivel, item } = detalle.value;
        // Codigo vacio = quitar; la API reemplaza la lista completa
        const lista = Object.entries(homologacion.codigos)
            .map(([portalId, codigo]) => ({ portal_id: Number(portalId), codigo: norm(codigo) }))
            .filter((p) => p.codigo !== '');

        homologacion.guardando = true;
        homologacion.error = '';
        try {
            const resultado = await store.dispatch('appLocalidades/homologarAction', {
                nivel, id: item.id, portales: lista,
            });
            detalle.value = { ...detalle.value, portales: resultado };
            const enColumna = columnas[nivel].items.find((i) => i.id === item.id);
            if (enColumna) enColumna.portales_count = resultado.length;
            homologacion.active = false;
            showAlert('Homologación actualizada', 'success');
        } catch (error) {
            homologacion.error = mensajeError(error, 'No se pudo guardar la homologación');
        } finally {
            homologacion.guardando = false;
        }
    };

    // --- Inicio ----------------------------------------------------------
    const iniciar = async () => {
        try {
            await store.dispatch('appLocalidades/getNivelesAction');
        } catch (error) {
            showAlert('No se pudo cargar la configuración; la edición queda deshabilitada', 'warning');
        }
        await cargarColumna('paises');
    };

    const formatNumber = (value) => Number(value || 0).toLocaleString('es-CO');

    return {
        alertTable,
        niveles, portales, puedeEditar,
        columnas, seleccion, columnasVisibles,
        detalle, loadingDetalle, extrasDetalle,
        form, borrado, enUso, homologacion,
        padreDe, hijoDe, extrasDe, infoNivel, extraLabel, nombrePortal, usoEntradas, formatNumber,
        iniciar, cargarColumna, cargarMas, buscarEnColumna, seleccionar,
        irAMiga, cerrarDetalle,
        abrirCrear, abrirEditar, cerrarForm, guardarForm,
        abrirBorrar, cerrarBorrar, confirmarBorrar,
        abrirHomologar, cerrarHomologar, guardarHomologacion,
    };
}
